import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQueryString } from "hooks/general-hooks";
import { getOverview } from "api/data_parser/champion_overview.js";
import { getRankings } from "api/data_parser/ranking_stats.js";
import { useLocation, useParams } from "react-router-dom";
import {
  useValidatedChampionProfileParams,
  useChampionOverview,
  useMultipleChampionOverview,
  useMultipleChampionRankingStats,
  useChampionRankingStats,
  useAramUrfData,
} from "lib/hooks/championProfileHooks";
import { useGetChampionOTPStats } from "@ugg/shared/api/requests/champions/otp";
import { getQueryParams, getForcedParams, getQueueTypeNameFromQueryParam } from "lib/specialGameMode";
import Build from "components/Champions/RecommendedBuild/Build";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import ArenaBuild from "components/Champions/RecommendedBuild/ArenaBuild";

const champion_transform_map = {
  141: [100141, 200141],
};

function getDefaultChampionTransformId(championId) {
  return champion_transform_map[championId] ? champion_transform_map[championId][0] : championId;
}

function isChampionTransform(championId) {
  return championId > 10000;
}

function getChampionOverview(championId, championOverviews, isAP, isTank, isAD, isCrit, isLethality, isOnHit) {
  if (isAP) {
    return championOverviews?.[championId]?.apChampionOverview;
  } else if (isTank) {
    return championOverviews?.[championId]?.tankChampionOverview;
  } else if (isAD) {
    return championOverviews?.[championId]?.adChampionOverview;
  } else if (isCrit) {
    return championOverviews?.[championId]?.critChampionOverview;
  } else if (isLethality) {
    return championOverviews?.[championId]?.lethalityChampionOverview;
  } else if (isOnHit) {
    return championOverviews?.[championId]?.onHitChampionOverview;
  } else {
    return championOverviews?.[championId]?.championOverview;
  }
}

function shouldEnableAP(filteredOverview, filteredAPOverview, mythicItems, bootItems, isAP) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const apKeyItem = filteredAPOverview?.rec_core_items?.items
    ? getClassifyItem(filteredAPOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableAP =
    filteredAPOverview &&
    defaultKeyItem &&
    apKeyItem &&
    !(apKeyItem === defaultKeyItem) &&
    filteredAPOverview?.item_options_1?.length > 1 &&
    filteredAPOverview?.item_options_2?.length > 1 &&
    filteredAPOverview?.item_options_3?.length > 1 &&
    filteredAPOverview?.rec_starting_items?.matches > 9 &&
    filteredAPOverview?.rec_core_items?.matches > 9;
  return shouldEnableAP;
}

function shouldEnableTank(filteredOverview, filteredTankOverview, mythicItems, bootItems) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const tankKeyItem = filteredTankOverview?.rec_core_items?.items
    ? getClassifyItem(filteredTankOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableTank =
    filteredTankOverview &&
    defaultKeyItem &&
    tankKeyItem &&
    !(tankKeyItem === defaultKeyItem) &&
    filteredTankOverview?.item_options_1?.length > 1 &&
    filteredTankOverview?.item_options_2?.length > 1 &&
    filteredTankOverview?.item_options_3?.length > 1 &&
    filteredTankOverview?.rec_starting_items?.matches > 9 &&
    filteredTankOverview?.rec_core_items?.matches > 9;

  return shouldEnableTank;
}

function shouldEnableAD(filteredOverview, filteredADOverview, mythicItems, bootItems) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const adKeyItem = filteredADOverview?.rec_core_items?.items
    ? getClassifyItem(filteredADOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableAD =
    filteredADOverview &&
    defaultKeyItem &&
    adKeyItem &&
    !(adKeyItem === defaultKeyItem) &&
    filteredADOverview?.item_options_1?.length > 1 &&
    filteredADOverview?.item_options_2?.length > 1 &&
    filteredADOverview?.item_options_3?.length > 1 &&
    filteredADOverview?.rec_starting_items?.matches > 9 &&
    filteredADOverview?.rec_core_items?.matches > 9;

  return shouldEnableAD;
}

function shouldEnableCrit(filteredOverview, filteredCritOverview, mythicItems, bootItems) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const critKeyItem = filteredCritOverview?.rec_core_items?.items
    ? getClassifyItem(filteredCritOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableCrit =
    filteredCritOverview &&
    defaultKeyItem &&
    critKeyItem &&
    !(critKeyItem === defaultKeyItem) &&
    filteredCritOverview?.item_options_1?.length > 1 &&
    filteredCritOverview?.item_options_2?.length > 1 &&
    filteredCritOverview?.item_options_3?.length > 1 &&
    filteredCritOverview?.rec_starting_items?.matches > 9 &&
    filteredCritOverview?.rec_core_items?.matches > 9;

  return shouldEnableCrit;
}

function shouldEnableLethality(filteredOverview, filteredLethalityOverview, mythicItems, bootItems) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const lethalityKeyItem = filteredLethalityOverview?.rec_core_items?.items
    ? getClassifyItem(filteredLethalityOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableLethality =
    filteredLethalityOverview &&
    defaultKeyItem &&
    lethalityKeyItem &&
    !(lethalityKeyItem === defaultKeyItem) &&
    filteredLethalityOverview?.item_options_1?.length > 1 &&
    filteredLethalityOverview?.item_options_2?.length > 1 &&
    filteredLethalityOverview?.item_options_3?.length > 1 &&
    filteredLethalityOverview?.rec_starting_items?.matches > 9 &&
    filteredLethalityOverview?.rec_core_items?.matches > 9;

  return shouldEnableLethality;
}

function shouldEnableOnHit(filteredOverview, filteredOnHitOverview, mythicItems, bootItems) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const onHitKeyItem = filteredOnHitOverview?.rec_core_items?.items
    ? getClassifyItem(filteredOnHitOverview?.rec_core_items?.items, mythicItems, bootItems)
    : 0;
  const shouldEnableOnHit =
    filteredOnHitOverview &&
    defaultKeyItem &&
    onHitKeyItem &&
    !(onHitKeyItem === defaultKeyItem) &&
    filteredOnHitOverview?.item_options_1?.length > 1 &&
    filteredOnHitOverview?.item_options_2?.length > 1 &&
    filteredOnHitOverview?.item_options_3?.length > 1 &&
    filteredOnHitOverview?.rec_starting_items?.matches > 9 &&
    filteredOnHitOverview?.rec_core_items?.matches > 9;

  return shouldEnableOnHit;
}

function getClassifyItem(items, mythicItems, bootItems) {
  if (!items || !mythicItems || !bootItems) {
    return 0;
  }
  let mythicFilter = items.filter((item) => mythicItems.includes(item));
  if (mythicFilter.length) {
    return mythicFilter[0];
  }
  var normal = 0;
  var others = 0;
  for (let i = 0; i < items.length; i++) {
    if (mythicItems.includes(items[i])) {
      return items[i];
    } else if (!bootItems.includes(items[i]) && normal === 0) {
      normal = items[i];
    } else {
      others = items[i];
    }
  }
  return normal != 0 ? normal : others;
}

const Overview = (props) => {
  //useparams from react router to get the value of multi
  const urlParams = useParams();
  const location = useLocation();

  const { page, championId, role, championData } = props;

  let queryParams = useQueryString();
  let forcedParams = {};

  // Special Game Mode Handling
  if (getQueryParams(location.pathname, queryParams)) {
    queryParams = getQueryParams(location.pathname, queryParams);
  }

  if (getForcedParams(location)) {
    forcedParams = getForcedParams(location);
  }

  const { useRiotMythicItems, useRiotBootItems } = getRiotAssetsContext();
  const { data: mythicItems } = useRiotMythicItems({ ssr: true });
  const { data: bootItems } = useRiotBootItems({ ssr: true });

  const { validatedParams } = useValidatedChampionProfileParams(championId, "build", { keepDefaultParams: true, ssr: true });

  const {
    data: rankingStats,
    loading: fetchingRankingStats,
    error: errorRankingStats,
  } = useChampionRankingStats(championId, { ssr: true, forcedParams: forcedParams });

  const {
    data: otpStats,
    loading: fetchingOtpStats,
    error: errorOtpStats,
  } = useGetChampionOTPStats(championId, { ssr: false, forcedParams: forcedParams });

  const params = {
    ...validatedParams,
    ...queryParams,
    ...forcedParams,
    role,
  };

  const championOverviews = useMultipleChampionOverview(championId, { ssr: true, forcedParams, queryParams: queryParams });
  const [forceDefaultData, setForceDefaultData] = useState(false);
  //Add other builds here as we go
  const isAP = urlParams.multi === "ap" ? true : false;
  const isTank = urlParams.multi === "tank" ? true : false;
  const isAD = urlParams.multi === "ad" ? true : false;
  const isCrit = urlParams.multi === "crit" ? true : false;
  const isLethality = urlParams.multi === "lethality" ? true : false;
  const isOnHit = urlParams.multi === "onhit" ? true : false;
  const isRed = urlParams.multi === "red" ? true : false;
  const isBlue = urlParams.multi === "blue" ? true : false;

  const version = params.patch;
  const queueTypeName = getQueueTypeNameFromQueryParam(queryParams.queueType);

  const tracking = isChampionTransform(getDefaultChampionTransformId(championId))
    ? [
        championOverviews[champion_transform_map[championId][0]].fetchingChampionOverview,
        championOverviews[champion_transform_map[championId][1]].fetchingChampionOverview,
      ]
    : [championOverviews[championId].fetchingChampionOverview];

  const firstLoad = useRef(true);
  const { buildKey, tabData } = useMemo(() => {
    let buildKey = getDefaultChampionTransformId(championId);
    const tabData = {};
    const transformChampionId = getDefaultChampionTransformId(championId);
    if (isChampionTransform(transformChampionId)) {
      let maxWinRate = 0;
      for (const [key, value] of Object.entries(championOverviews)) {
        const filteredOverview = getOverview(value.championOverview, params);
        tabData[key] = [
          filteredOverview?.rec_runes?.active_perks[0],
          filteredOverview?.rec_core_items?.items
            ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
        ];
        if (filteredOverview && filteredOverview.win_rate > maxWinRate && firstLoad.current) {
          buildKey = key;
          maxWinRate = filteredOverview.win_rate;
        }
      }
      firstLoad.current = false;
    } else {
      const filteredOverview = getOverview(championOverviews[transformChampionId]?.championOverview, params);
      tabData["default"] = [
        filteredOverview?.rec_runes?.active_perks[0],
        filteredOverview?.rec_core_items?.items
          ? getClassifyItem(filteredOverview?.rec_core_items?.items, mythicItems, bootItems)
          : 0,
        100000000,
      ];
      const filteredAPOverview = getOverview(championOverviews[transformChampionId]?.apChampionOverview, params);
      const enableAP = shouldEnableAP(filteredOverview, filteredAPOverview, mythicItems, bootItems, isAP);
      if (filteredAPOverview && filteredOverview && enableAP) {
        tabData["ap"] = [
          filteredAPOverview?.rec_runes?.active_perks[0],
          filteredAPOverview?.rec_core_items?.items
            ? getClassifyItem(filteredAPOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredAPOverview.matches ? filteredAPOverview.matches : 0,
        ];
      }
      const filteredTankOverview = getOverview(championOverviews[transformChampionId]?.tankChampionOverview, params);
      const enableTank = shouldEnableTank(filteredOverview, filteredTankOverview, mythicItems, bootItems, isTank);
      if (filteredTankOverview && filteredOverview && enableTank) {
        tabData["tank"] = [
          filteredTankOverview?.rec_runes?.active_perks[0],
          filteredTankOverview?.rec_core_items?.items
            ? getClassifyItem(filteredTankOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredTankOverview.matches ? filteredTankOverview.matches : 0,
        ];
      }
      const filteredADOverview = getOverview(championOverviews[transformChampionId]?.adChampionOverview, params);
      const enableAD = shouldEnableAD(filteredOverview, filteredADOverview, mythicItems, bootItems, isAD);
      if (filteredADOverview && filteredOverview && enableAD) {
        tabData["ad"] = [
          filteredADOverview?.rec_runes?.active_perks[0],
          filteredADOverview?.rec_core_items?.items
            ? getClassifyItem(filteredADOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredADOverview.matches ? filteredADOverview.matches : 0,
        ];
      }
      const filteredCritOverview = getOverview(championOverviews[transformChampionId]?.critChampionOverview, params);
      const enableCrit = shouldEnableCrit(filteredOverview, filteredCritOverview, mythicItems, bootItems, isCrit);
      if (filteredCritOverview && filteredOverview && enableCrit) {
        tabData["crit"] = [
          filteredCritOverview?.rec_runes?.active_perks[0],
          filteredCritOverview?.rec_core_items?.items
            ? getClassifyItem(filteredCritOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredCritOverview.matches ? filteredCritOverview.matches : 0,
        ];
      }
      const filteredLethalityOverview = getOverview(championOverviews[transformChampionId]?.lethalityChampionOverview, params);
      const enableLethality = shouldEnableLethality(
        filteredOverview,
        filteredLethalityOverview,
        mythicItems,
        bootItems,
        isLethality,
      );
      if (filteredLethalityOverview && filteredOverview && enableLethality) {
        tabData["lethality"] = [
          filteredLethalityOverview?.rec_runes?.active_perks[0],
          filteredLethalityOverview?.rec_core_items?.items
            ? getClassifyItem(filteredLethalityOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredLethalityOverview.matches ? filteredLethalityOverview.matches : 0,
        ];
      }
      const filteredOnHitOverview = getOverview(championOverviews[transformChampionId]?.onHitChampionOverview, params);
      const enableOnHit = shouldEnableOnHit(filteredOverview, filteredOnHitOverview, mythicItems, bootItems, isOnHit);
      if (filteredOnHitOverview && filteredOverview && enableOnHit) {
        tabData["onhit"] = [
          filteredOnHitOverview?.rec_runes?.active_perks[0],
          filteredOnHitOverview?.rec_core_items?.items
            ? getClassifyItem(filteredOnHitOverview?.rec_core_items?.items, mythicItems, bootItems)
            : 0,
          filteredOnHitOverview.matches ? filteredOnHitOverview.matches : 0,
        ];
      }
      // Use this to test onhit
      // tabData["1onhit"] = [
      //   filteredOnHitOverview?.rec_runes?.active_perks[0],
      //   filteredOnHitOverview?.rec_core_items?.items
      //     ? getClassifyItem(filteredOnHitOverview?.rec_core_items?.items, mythicItems, bootItems)
      //     : 0,
      //   100,
      // ];
    }

    //Limit multi builds to 3
    if (Object.keys(tabData).length > 4) {
      let min = 100000000;
      let minKey;
      for (const key in tabData) {
        const matches = tabData[key][2] ?? 0;
        if (matches < min) {
          min = matches;
          minKey = key;
        }
      }
      delete tabData[minKey];
    }

    return { buildKey, tabData };
  }, [championOverviews, ...tracking, JSON.stringify(params)]);

  const [defaultBuild, setDefaultBuild] = useState(buildKey);
  const championOverview = getChampionOverview(
    defaultBuild,
    championOverviews,
    isAP,
    isTank,
    isAD,
    isCrit,
    isLethality,
    isOnHit,
    params,
  );

  useEffect(() => {
    setDefaultBuild(buildKey);
  }, [buildKey]);

  const filteredChampionOverview = getOverview(championOverview, params);
  const filteredRankingStats = getRankings(rankingStats, params);
  const fetchingData = !isChampionTransform(getDefaultChampionTransformId(championId))
    ? (!championOverview && championOverviews[championId].fetchingChampionOverview) || fetchingRankingStats
    : (!championOverview &&
        championOverviews[champion_transform_map[championId][0]].fetchingChampionOverview &&
        championOverviews[champion_transform_map[championId][1]].fetchingChampionOverview) ||
      fetchingRankingStats;

  const { allChampionStatsARAM, allChampionStatsURF } = useAramUrfData(queryParams.queueType, version);

  let renderAdditionalStats = false;
  let championModifications = null;
  let normalizedName = championData.name.replace(/\./g, "").replace(/ /g, "").replace(/'/g, "").toLowerCase();

  if (allChampionStatsARAM || allChampionStatsURF) {
    if (queueTypeName === "ARAM") {
      if (allChampionStatsARAM.hasOwnProperty(normalizedName)) {
        renderAdditionalStats =
          ["urf", "arurf", "normal_aram"].includes(queryParams.queueType) && allChampionStatsARAM[normalizedName]["changes"];
        championModifications = allChampionStatsARAM[normalizedName];
      }
    } else if (queueTypeName === "ARURF" || queueTypeName === "URF") {
      if (allChampionStatsURF.hasOwnProperty(normalizedName)) {
        renderAdditionalStats =
          ["urf", "arurf", "normal_aram"].includes(queryParams.queueType) && allChampionStatsURF[normalizedName]["changes"];
        championModifications = allChampionStatsURF[normalizedName];
      }
    }
  }

  const componentProps = {
    ...props,
    role,
    version,
    fetchingData,
    championOverview: filteredChampionOverview,
    otpStats,
    filteredRankingStats,
    championId,
    filters: queryParams,
    missingMatchup: forceDefaultData,
    queryParams,
    queueTypeName,
    renderAdditionalStats,
    championModifications,
    tabData,
    defaultBuild,
    setDefaultBuild,
    isAP,
    isTank,
    isAD,
    isCrit,
    isLethality,
    isOnHit,
    isRed,
    isBlue,
  };

  if (page === "overview_arena") {
    return <ArenaBuild {...componentProps} />;
  }

  return <Build {...componentProps} />;
};

export default Overview;
